import type { AppConfig } from '@/config/env';

import { ApiError } from './errors';
import { WEB_CLIENT_MACHINE_KEY } from './machine-key';
import type { Credentials, PortalResult } from './types';

/**
 * Request-header names expected by the Portal (mirrors
 * Server/Portal/Areas/SuiteAPI/RequestHeader.cs). The Portal authenticates the
 * brand from the `brandID` header and the user from the `userName`/`userPWD`
 * headers plus the request body.
 */
const Header = {
  UserName: 'userName',
  UserPassword: 'userPWD',
  MachineKey: 'machineKey',
  BrandId: 'brandID',
  ClientVersion: 'clientVersion',
  Authorization: 'Authorization',
} as const;

export interface RequestOptions {
  method?: 'GET' | 'PUT' | 'POST' | 'DELETE';
  /** Relative path beginning with '/', e.g. '/SuiteAPI/User/LoginV1'. */
  path: string;
  /** Optional query parameters. */
  query?: Record<string, string>;
  /** Optional JSON body. */
  body?: unknown;
  /** Whether to attach the current user credentials as auth headers. */
  authenticated?: boolean;
  /** Cancellation support. */
  signal?: AbortSignal;
}

export interface UploadRequestOptions {
  method?: 'PUT' | 'POST';
  path: string;
  query?: Record<string, string>;
  formData: FormData;
  authenticated?: boolean;
  signal?: AbortSignal;
  onProgress?: UploadProgressCallback;
}

export type UploadProgressCallback = (percentage: number) => void;

/** Called when the Portal reports that the session is no longer valid. */
export type UnauthorizedHandler = () => void;

/**
 * Centralised, typed Portal API client.
 *
 * All Portal access flows through this class so credential handling, error
 * normalisation and unauthorized handling live in exactly one place instead of
 * being scattered across React components.
 */
export class PortalClient {
  private readonly config: AppConfig;
  private credentials: Credentials | null = null;
  private jwt: string | null = null;
  private onUnauthorized: UnauthorizedHandler | null = null;

  public constructor(config: AppConfig) {
    this.config = config;
  }

  /** Registers a callback invoked once when the Portal rejects the session. */
  public setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
    this.onUnauthorized = handler;
  }

  /** Stores the credentials re-sent on every authenticated request. */
  public setCredentials(credentials: Credentials | null): void {
    this.credentials = credentials;
  }

  public hasCredentials(): boolean {
    return this.credentials !== null;
  }

  /**
   * Stores (or clears) a JWT bearer token. When set, authenticated requests send
   * `Authorization: Bearer <jwt>` instead of the `userName`/`userPWD` headers
   * (mirrors the alternative auth path in SuiteApiAuthorizationFilter). Setting a
   * token clears any classic credentials so exactly one auth scheme is active.
   */
  public setJwt(jwt: string | null): void {
    this.jwt = jwt;
    if (jwt) {
      this.credentials = null;
    }
  }

  public hasJwt(): boolean {
    return this.jwt !== null;
  }

  private buildUrl(path: string, query?: Record<string, string>): string {
    const url = `${this.config.apiBaseUrl}${path}`;
    if (!query || Object.keys(query).length === 0) {
      return url;
    }
    const params = new URLSearchParams(query);
    return `${url}?${params.toString()}`;
  }

  private buildHeaders(authenticated: boolean): Headers {
    const headers = new Headers({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      [Header.BrandId]: this.config.brandId,
      [Header.ClientVersion]: this.config.clientVersion,
      [Header.MachineKey]: WEB_CLIENT_MACHINE_KEY,
    });

    if (authenticated) {
      if (this.jwt) {
        headers.set(Header.Authorization, `Bearer ${this.jwt}`);
      } else if (this.credentials) {
        headers.set(Header.UserName, this.credentials.email);
        headers.set(Header.UserPassword, this.credentials.password);
      }
    }

    return headers;
  }

  /**
   * Executes a request and returns the parsed {@link PortalResult}.
   *
   * @throws {ApiError} on network failure, cancellation, HTTP error, or a
   * non-success Portal result.
   */
  public async request<T = undefined>(options: RequestOptions): Promise<PortalResult<T>> {
    const { method = 'PUT', path, query, body, authenticated = false, signal } = options;

    let response: Response;
    try {
      const init: RequestInit = {
        method,
        headers: this.buildHeaders(authenticated),
      };
      if (body !== undefined) {
        init.body = JSON.stringify(body);
      }
      if (signal) {
        init.signal = signal;
      }
      response = await fetch(this.buildUrl(path, query), init);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') {
        throw new ApiError('aborted', 'aborted', { cause });
      }
      // Do not include the URL or payload — keep diagnostics generic.
      throw new ApiError('network', 'network', { cause });
    }

    if (response.status === 401 || response.status === 403) {
      this.notifyUnauthorized();
      throw new ApiError('unauthorized', 'sessionExpired', { httpStatus: response.status });
    }

    if (!response.ok) {
      throw new ApiError('server', 'serverUnavailable', { httpStatus: response.status });
    }

    let payload: PortalResult<T>;
    try {
      payload = (await response.json()) as PortalResult<T>;
    } catch (cause) {
      throw new ApiError('server', 'invalidResponse', { cause });
    }

    return payload;
  }

  /** Notifies the registered handler that the session is no longer valid. */
  public notifyUnauthorized(): void {
    if (this.onUnauthorized) {
      this.onUnauthorized();
    }
  }

  /**
   * Executes a multipart/form-data request and returns the parsed {@link PortalResult}.
   * Does not set Content-Type so the browser can set the correct multipart boundary.
   *
   * @throws {ApiError} on network failure, cancellation, HTTP error, or a non-success Portal result.
   */
  public async uploadFormData<T = undefined>(options: UploadRequestOptions): Promise<PortalResult<T>> {
    if (options.onProgress) {
      return this.uploadFormDataWithProgress(options);
    }

    const { method = 'PUT', path, query, formData, authenticated = false, signal } = options;
    const headers = this.buildHeaders(authenticated);
    headers.delete('Content-Type'); // let fetch set multipart/form-data with boundary

    let response: Response;
    try {
      response = await fetch(this.buildUrl(path, query), {
        method,
        headers,
        body: formData,
        ...(signal ? { signal } : {}),
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') {
        throw new ApiError('aborted', 'aborted', { cause });
      }
      throw new ApiError('network', 'network', { cause });
    }

    if (response.status === 401 || response.status === 403) {
      this.notifyUnauthorized();
      throw new ApiError('unauthorized', 'sessionExpired', { httpStatus: response.status });
    }

    if (!response.ok) {
      throw new ApiError('server', 'serverUnavailable', { httpStatus: response.status });
    }

    let payload: PortalResult<T>;
    try {
      payload = (await response.json()) as PortalResult<T>;
    } catch (cause) {
      throw new ApiError('server', 'invalidResponse', { cause });
    }

    return payload;
  }

  private async uploadFormDataWithProgress<T>(options: UploadRequestOptions): Promise<PortalResult<T>> {
    const { method = 'PUT', path, query, formData, authenticated = false, signal, onProgress } = options;
    const headers = this.buildHeaders(authenticated);
    headers.delete('Content-Type');

    return new Promise<PortalResult<T>>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      let settled = false;

      const cleanup = (): void => {
        if (signal) {
          signal.removeEventListener('abort', abortRequest);
        }
      };

      const fail = (error: ApiError): void => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        reject(error);
      };

      const abortRequest = (): void => {
        xhr.abort();
      };

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress?.((event.loaded / event.total) * 100);
        }
      });
      xhr.onerror = () => fail(new ApiError('network', 'network'));
      xhr.onabort = () => fail(new ApiError('aborted', 'aborted'));
      xhr.onload = () => {
        if (settled) {
          return;
        }

        const status = xhr.status;
        if (status === 401 || status === 403) {
          this.notifyUnauthorized();
          fail(new ApiError('unauthorized', 'sessionExpired', { httpStatus: status }));
          return;
        }

        if (status < 200 || status >= 300) {
          fail(new ApiError('server', 'serverUnavailable', { httpStatus: status }));
          return;
        }

        try {
          const payload = JSON.parse(xhr.responseText) as PortalResult<T>;
          settled = true;
          cleanup();
          onProgress?.(100);
          resolve(payload);
        } catch (cause) {
          fail(new ApiError('server', 'invalidResponse', { cause }));
        }
      };

      if (signal?.aborted) {
        fail(new ApiError('aborted', 'aborted'));
        return;
      }

      try {
        xhr.open(method, this.buildUrl(path, query), true);
        headers.forEach((value, key) => xhr.setRequestHeader(key, value));
        signal?.addEventListener('abort', abortRequest, { once: true });
        xhr.send(formData);
      } catch (cause) {
        fail(new ApiError('network', 'network', { cause }));
      }
    });
  }

  /** Executes an authenticated request and returns the raw response body as a blob. */
  public async download(options: RequestOptions): Promise<Blob> {
    const { method = 'GET', path, query, body, authenticated = false, signal } = options;
    const headers = this.buildHeaders(authenticated);
    headers.set('Accept', 'application/octet-stream');

    let response: Response;
    try {
      response = await fetch(this.buildUrl(path, query), {
        method,
        headers,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        ...(signal ? { signal } : {}),
      });
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') {
        throw new ApiError('aborted', 'aborted', { cause });
      }
      throw new ApiError('network', 'network', { cause });
    }

    if (response.status === 401 || response.status === 403) {
      this.notifyUnauthorized();
      throw new ApiError('unauthorized', 'sessionExpired', { httpStatus: response.status });
    }

    if (!response.ok) {
      throw new ApiError('server', 'serverUnavailable', { httpStatus: response.status });
    }

    return response.blob();
  }
}
